// src/pages/LoginPage.js

import React, { useState } from 'react';
import authApi from '../authApi'; // Make sure you are importing authApi
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    try {
        // THIS IS THE CRITICAL LINE: It MUST be authApi.post
        const response = await authApi.post('/api/token/', {
            username,
            password,
        });

        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);
        navigate('/dashboard');
    } catch (err) {
      setError('Login failed. Please check your username and password.');
      console.error('Login failed', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="flex items-center justify-center min-h-screen bg-cover bg-center bg-no-repeat relative"
      style={{ backgroundImage: "url('/images/login-bg.png')" }}
    >
      {/* Semi-transparent dark overlay */}
      <div className="absolute inset-0 bg-gray-900/60"></div>
      
      {/* Glassmorphism Form Container */}
      <div className="relative w-full max-w-md p-6 sm:p-8 space-y-6 bg-gray-900/10 backdrop-blur-md shadow-2xl rounded-2xl border border-white/20 z-10 mx-4">
        <h2 className="text-2xl font-bold text-center text-white">SoloSync Login</h2>
        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label htmlFor="username" className="text-sm font-medium text-gray-100">
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 mt-1 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500 text-white bg-gray-800 text-base"
              required
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="text-sm font-medium text-gray-100">
                Password
              </label>
              <Link 
                to="/forgot-password" 
                className="text-xs text-teal-400 hover:text-teal-300 font-medium transition-colors py-1"
                id="forgot-password-link"
              >
                Forgot Password?
              </Link>
            </div>
            <div className="relative mt-1">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500 text-white bg-gray-800 pr-12 text-base"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 px-3 flex items-center justify-center text-gray-400 hover:text-white transition-colors focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-red-400 font-medium">{error}</p>}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2.5 font-semibold text-gray-900 bg-gradient-to-r from-teal-400 to-teal-500 rounded-md hover:from-teal-300 hover:to-teal-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </div>
        </form>
        <p className="text-center text-sm text-gray-400">
          Don't have an account?{' '}
          <Link to="/register" className="text-teal-400 hover:text-teal-300 font-medium transition-colors">Register</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;